<template>
	<div class="min-h-screen bg-gray-50 px-3 py-4 sm:p-6 lg:p-8">
		<div class="max-w-7xl mx-auto">
			<!-- Header Section -->
			<div class="text-center mb-6 lg:mb-10">
				<div
					class="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-primary rounded-xl mb-3 sm:mb-4 shadow-sm"
				>
					<i class="fas fa-shield-alt text-white"></i>
				</div>
				<h1
					class="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2 sm:mb-3"
				>
					Insurance {{ selectedCoverType.name }} Products
				</h1>
				<p class="text-sm sm:text-base text-gray-600 max-w-3xl mx-auto">
					Discover comprehensive insurance solutions tailored to your
					unique needs with competitive rates and trusted coverage
				</p>
			</div>

			<!-- Error Messages -->
			<div
				v-if="Object.keys(errors).length"
				class="mb-4 sm:mb-6 bg-red-50 border-l-4 border-red-400 rounded p-3 sm:p-4 shadow-sm"
			>
				<div class="flex items-start">
					<div class="flex-shrink-0">
						<i class="fas fa-exclamation-circle text-red-400"></i>
					</div>
					<div class="ml-3">
						<h3 class="text-sm font-medium text-red-800">
							Please correct the following errors:
						</h3>
						<div class="mt-1 sm:mt-2 text-sm text-red-700">
							<ul
								class="list-disc list-inside space-y-0.5 sm:space-y-1"
							>
								<li
									v-for="(error, key) in errors"
									:key="key"
									class="break-words"
								>
									{{ error }}
								</li>
							</ul>
						</div>
					</div>
				</div>
			</div>

			<!-- Filter Section -->
			<div
				class="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-5 mb-4 sm:mb-6"
			>
				<!-- Filter Header -->
				<div class="flex justify-between items-center mb-3 sm:mb-4">
					<h3
						class="text-sm sm:text-base font-semibold text-gray-800 flex items-center"
					>
						<i class="fas fa-sliders-h mr-2 text-primary"></i>
						Filter Products
					</h3>
					<button
						@click="clearFilters"
						class="text-xs sm:text-sm text-primary hover:text-primary-dark font-medium"
					>
						Clear Filters
					</button>
				</div>

				<!-- Filter Controls -->
				<div class="grid grid-cols-1 gap-3 sm:gap-4">
					<div class="space-y-1">
						<label
							for="underwriter"
							class="block text-xs sm:text-sm font-medium text-gray-700"
							>Underwriter</label
						>
						<div class="relative">
							<select
								v-model="filters.underwriter"
								id="underwriter"
								class="w-full appearance-none bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-xs sm:text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
							>
								<option value="All">All Underwriters</option>
								<option
									v-for="underwriter in filterOptions.underwriters"
									:key="underwriter.name"
									:value="underwriter.name"
								>
									{{ underwriter.name }}
								</option>
							</select>
							<div
								class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
							>
								<i
									class="fas fa-chevron-down text-gray-400"
								></i>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Products Grid -->
			<div
				class="products-list grid grid-cols-2 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-5 mb-6 sm:mb-8"
			>
				<div
					v-for="product in insuranceClassData"
					:key="product.id"
					class="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-md hover:border-gray-300 animate-fade-in-up flex flex-col aspect-square sm:aspect-auto"
				>
					<!-- Logo Section -->
					<div
						class="relative bg-white p-1.5 sm:p-4 border-b border-gray-100"
					>
						<div
							class="flex items-center justify-center h-10 sm:h-24 mb-0.5 sm:mb-1"
						>
							<div
								class="relative w-10 h-10 sm:w-20 sm:h-20 rounded-lg overflow-hidden shadow-sm ring-1 ring-gray-200 group-hover:ring-gray-300 transition-colors"
							>
								<div v-if="product.productLogo">
									<img
										:src="product.productLogo"
										:alt="`${product.productName} Logo`"
										class="w-full h-full object-cover bg-white"
									/>
								</div>
								<div
									v-else
									class="w-full h-full bg-gray-100 flex items-center justify-center"
								>
									<i
										class="fas fa-shield-alt text-gray-400"
									></i>
								</div>
							</div>
						</div>
					</div>

					<!-- Content Section -->
					<div class="p-1.5 sm:p-4 flex flex-col flex-grow">
						<div class="mb-1 sm:mb-2">
							<h3
								class="text-xs sm:text-lg font-semibold text-gray-900 mb-0.5 sm:mb-1 group-hover:text-primary transition-colors line-clamp-2"
							>
								{{ product.productName }}
							</h3>
						</div>

						<!-- Product Details -->
						<div class="mb-1.5 sm:mb-4">
							<div
								class="bg-gray-50 rounded-md p-1.5 sm:p-2 transition-colors group-hover:bg-blue-50"
							>
								<p
									class="text-xs font-medium text-gray-500 uppercase tracking-wider mb-0.5"
								>
									Premium
								</p>
								<p
									class="text-xs sm:text-base font-semibold text-gray-900"
								>
									Kes {{ formatPremium(product) }}
								</p>
								<!-- Display premium type indicator for third-party -->
								<p
									v-if="selectedCoverType === 'third-party'"
									class="text-xs text-primary font-medium mt-0.5"
								>
									Fixed Rate
								</p>
							</div>
							<!--              <div class="bg-gray-50 rounded-md p-1.5 sm:p-2 mt-1.5 sm:mt-2 transition-colors group-hover:bg-blue-50">-->
							<!--                <p class="text-xs font-medium text-gray-500 uppercase tracking-wider mb-0.5">Underwriter</p>-->
							<!--                <p v-if="product.underwriter" class="text-xs font-semibold text-gray-900 truncate" :title="getUnderwriterName(product)">-->
							<!--                  {{ product.underwriter[0].name}}-->
							<!--                </p>-->
							<!--              </div>-->
						</div>

						<!-- Action Buttons -->
						<div class="mt-auto space-y-1 sm:space-y-2">
							<!-- Primary Actions -->
							<div
								class="flex flex-col sm:flex-row gap-1 sm:gap-2"
							>
								<button
									@click="buyProduct(product)"
									class="w-full bg-primary hover:bg-primary-dark text-white text-xs font-medium py-1.5 sm:py-2 px-1.5 sm:px-2 rounded-md transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-primary focus:ring-offset-1 order-1 sm:order-2"
								>
									<i class="fas fa-lock mr-0.5 inline"></i>
									Buy
								</button>

								<button
									@click="viewDetails(product)"
									class="w-full bg-white hover:bg-gray-50 text-gray-700 text-xs font-medium py-1.5 sm:py-2 px-1.5 sm:px-2 rounded-md border border-gray-300 transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-gray-400 focus:ring-offset-1 order-2 sm:order-1"
								>
									<i class="fas fa-eye mr-0.5 inline"></i>
									<span class="hidden sm:inline"
										>Details</span
									>
									<span class="sm:hidden">View</span>
								</button>
							</div>

							<!-- Secondary Actions -->
							<div class="hidden sm:flex gap-2 items-baseline">
								<button
									@click="toggleComparison(product)"
									:class="[
										'flex-1 text-xs sm:text-sm font-medium py-1.5 px-2 sm:px-3 rounded-md transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-offset-1',
										isProductInComparison(product)
											? 'bg-red-500 hover:bg-red-600 text-white focus:ring-red-500'
											: 'bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 focus:ring-indigo-500',
									]"
								>
									<i
										:class="
											isProductInComparison(product)
												? 'fas fa-heart'
												: 'far fa-heart'
										"
										class="mr-1 inline"
									></i>
									{{
										isProductInComparison(product)
											? 'Remove'
											: 'Compare'
									}}
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Navigation Footer -->
			<div
				class="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-5"
			>
				<div
					class="flex flex-col sm:flex-row justify-between items-center gap-3"
				>
					<button
						@click="previous"
						class="w-full sm:w-auto flex items-center justify-center px-4 py-2 text-xs sm:text-sm text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-md font-medium transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-gray-400 focus:ring-offset-1 order-2 sm:order-1"
					>
						<i class="fas fa-arrow-left mr-1"></i>
						Previous Step
					</button>
				</div>
			</div>
		</div>

		<!-- Comparison Modal -->
		<CompareProductsModal
			:visible="showComparisonModal"
			@close="closeComparisonModal"
		/>
	</div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import { FetchClassProductDetails } from '../../../api/insurance';
import { loadState, saveState } from '../../../../../libs/persist';
import CompareProductsModal from './CompareProductsModal.vue';

export default {
	name: 'ProductQuotation',
	components: {
		CompareProductsModal,
	},
	data() {
		return {
			filters: {
				underwriter: 'All',
			},
			filterOptions: {
				underwriters: [],
			},
			showComparisonModal: false,
		};
	},
	computed: {
		...mapState('insurance/motorPrivate', [
			'quotedProducts',
			'selectedCoverType',
			'errors',
			'vehicleValue',
			'coverDetails',
		]),
		...mapState('comparison', ['selectedComparisonProducts']),
		...mapGetters('product', ['selectedClass', 'selectedCoverType']),
		insuranceClassData() {
			return this.$store.getters['product/selectInsuranceClass'];
		},
		filteredProducts() {
			return this.insuranceClassData.filter((product) => {
				const underwriterName =
					product.underwriter &&
					product.underwriter[0] &&
					product.underwriter[0].name
						? product.underwriter[0].name
						: null;
				return (
					this.filters.underwriter === 'All' ||
					underwriterName === this.filters.underwriter
				);
			});
		},

		filterCoverTypeProducts() {
			const products = this.insuranceClassData.filter(
				(product) =>
					product.covertypeSystemRef == this.selectedCoverType.code,
			);
			return products;
		},
		formattedRateType() {
			return (product) => {
				if (product.basePremiumRate.rateType === 'percentage') {
					return `${(product.basePremiumRate.value * 100).toLocaleString()}%`;
				}
				return product.basePremiumRate.value.toLocaleString();
			};
		},
	},
	methods: {
		...mapActions('insurance/motorPrivate', [
			'fetchQuotations',
			'setSelectedProduct',
			'clearErrors',
			'setVehicleValue',
			'setErrors',
		]),

		...mapActions('product', ['setProduct', 'setProductCharges']),
		...mapActions('benefits', ['clearBenefitsData']),
		...mapActions('comparison', [
			'addComparisonProduct',
			'removeComparisonProduct',
		]),
		...mapActions('help', ['setEntities']),

		formatPremium(product) {
			if (!product.basePremium && product.basePremium !== 0) {
				return 'N/A';
			}
			return product.basePremium.toLocaleString();
		},

		getComparisonId(product) {
			const coverCode =
				this.selectedCoverType &&
				(this.selectedCoverType.code ||
					this.selectedCoverType.system_covertype_ref ||
					this.selectedCoverType);
			return `${product.productSystemRef}-${coverCode}`;
		},

		getUnderwriterName(product) {
			if (
				product.underwriter_details &&
				product.underwriter_details.name
			) {
				return product.underwriter_details.name;
			}
			if (product.underwriter) {
				const underwriter = Array.isArray(product.underwriter)
					? product.underwriter[0]
					: product.underwriter;
				return underwriter.name || 'N/A';
			}
			return 'N/A';
		},

		clearFilters() {
			this.filters.underwriter = 'All';
		},

		async buyProduct(product) {
			if (!this.selectedCoverType) {
				this.setErrors({
					selectedCoverType: 'Please select a cover type.',
				});
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Selection Required',
					message: 'Please select a cover type to proceed.',
					duration: 3000,
				});
				return;
			}

			const res = await FetchClassProductDetails(
				product.productSystemRef,
			);
			if (res.status) {
				this.clearBenefitsData();
				this.setProduct(res.data[0]);
				this.setProductCharges(product.product_charges);
				// this.$store.dispatch("productType/setProduct", res.data.result.data[0]);
				this.$store.dispatch('product/setProduct', product);

				if (product.basePremium) {
					saveState('basePremium', product.basePremium);
				}

				this.$router.push({ name: 'BasicCoverDetails' });
			}
		},

		async viewDetails(product) {
			const res = await FetchClassProductDetails(
				product.productSystemRef,
			);
			if (res.status) {
				this.$store.dispatch('product/setProduct', res.data[0]);
				this.setSelectedProduct(res.data[0]);
				this.setProductCharges(product.product_charges);
				this.$router.push({ name: 'ViewProductDetails' });
				Event.$emit('notifications:show', {
					type: 'success',
					title: 'Product Details Retrieved',
					message:
						'The product details have been successfully retrieved.',
					duration: 3000,
				});
			}
		},

		toggleComparison(product) {
			if (this.isProductInComparison(product)) {
				this.removeComparisonProduct(this.getComparisonId(product));
			} else {
				let underwriterName = 'N/A';
				if (
					product.underwriter_details &&
					product.underwriter_details.name
				) {
					underwriterName = product.underwriter_details.name;
				} else if (
					product.underwriter &&
					product.underwriter[0] &&
					product.underwriter[0].name
				) {
					underwriterName = product.underwriter[0].name;
				}

				const coreBenefits = (product.productBenefits || [])
					.filter((b) => b.benefitType === 'embedded')
					.map((b) => ({
						id: b._id,
						name: b.benefit_category_name || 'N/A',
						description:
							b.benefit_category_description || 'No description',
					}));

				const optionalBenefits = (product.productBenefits || [])
					.filter((b) => b.benefitType === 'optional')
					.map((b) => ({
						id: b._id,
						name: b.benefit_category_name || 'N/A',
						description:
							b.benefit_category_description || 'No description',
					}));

				let excesses = [];

				if (
					product.excessConfig &&
					product.excessConfig.options &&
					product.excessConfig.options.length
				) {
					excesses = product.excessConfig.options.map((o, idx) => ({
						id: o.id || idx,
						name: o.label || 'N/A',
					}));
				} else {
					excesses = [{ id: 0, name: 'Standard' }];
				}

				let basePremiumRateLabel = 'Not specified';
				if (
					product.basePremiumRate &&
					product.basePremiumRate.rateType &&
					product.basePremiumRate.value != null
				) {
					if (product.basePremiumRate.rateType === 'percentage') {
						basePremiumRateLabel =
							product.basePremiumRate.value + '%';
					} else {
						basePremiumRateLabel =
							'KSh ' + product.basePremiumRate.value;
					}
				}

				let premium = 0;
				premium = product.basePremium;

				const mappedProduct = {
					id: this.getComparisonId(product),
					name: product.productName || 'Unnamed Product',
					description: product.productDescription || 'No description',
					premium: premium,
					underwriter: underwriterName,
					coreBenefits: coreBenefits,
					optionalBenefits: optionalBenefits,
					coverType: this.selectedCoverType,
					productSystemRef: product.productSystemRef,
					underwriter_details: product.underwriter_details,
					excesses: excesses,
					basePremiumRateLabel: basePremiumRateLabel,
				};

				this.addComparisonProduct(mappedProduct);

				this.$nextTick(() => {
					this.showComparisonModal = true;
				});
			}
		},

		isProductInComparison(product) {
			const id = this.getComparisonId(product);
			return this.selectedComparisonProducts.some((p) => p.id === id);
		},

		closeComparisonModal() {
			this.showComparisonModal = false;
		},

		previous() {
			this.clearErrors();
			this.$router.push({ name: 'CoverTypeSelection' });
		},

		generateFilterOptions() {
			if (
				!this.insuranceClassData ||
				this.insuranceClassData.length === 0
			) {
				this.filterOptions.underwriters = [];
				return;
			}
			const underwriters = [
				...new Set(
					this.insuranceClassData
						.filter(
							(p) =>
								p.underwriter &&
								p.underwriter[0] &&
								p.underwriter[0].name,
						)
						.map((p) => p.underwriter[0].name),
				),
			].map((name) => ({ name }));
			this.filterOptions.underwriters = underwriters;
		},
	},

	watch: {
		insuranceClassData: {
			handler() {
				this.generateFilterOptions();
			},
			immediate: true,
			deep: true,
		},
	},

	async mounted() {
		await this.fetchQuotations();
		this.clearErrors();
		// console.log('===selectedCovertype===', this.selectedCoverType);
	},
};
</script>

<style scoped>
@keyframes fade-in-up {
	from {
		opacity: 0;
		transform: translateY(20px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.animate-fade-in-up {
	animation: fade-in-up 0.4s ease-out forwards;
}

.line-clamp-1 {
	display: -webkit-box;
	-webkit-line-clamp: 1;
	line-clamp: 1;
	-webkit-box-orient: vertical;
	overflow: hidden;
}

.line-clamp-2 {
	display: -webkit-box;
	-webkit-line-clamp: 2;
	line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
}

@media (max-width: 640px) {
	.text-2xl {
		font-size: 1.5rem;
	}
}

@media (prefers-reduced-motion: reduce) {
	.animate-fade-in-up {
		animation: none;
	}
}
</style>
